import { httpClient } from "@/app/client/httpClient";
import { captureException } from "@/plugins/sentry";
import { authStore } from "@/store/auth";
import { masterStore } from "@/store/master";
import { AppSatisfaction } from "@/types/accounts/AppSatisfaction";
import { TimingOfInAppReview } from "@/types/accounts/InAppReview";
import { DesiredWorkSchedule } from "@/types/desired_work_schedule/DesiredWorkSchedule";
import { Qualification } from "@/types/Master";
import { OSType } from "@/types/OSType";
import { PageableRequest } from "@/types/PageableRequest";
import { ReferPromotionApply } from "@/types/refer_promotion/ReferPromotion";
import { EditableUserItems } from "@/types/user/EditableUserItems";
import { RequestVerificationQueueBody } from "@/types/user/RequestVerificationQueueBody";

class AccountService {
  updateUserData() {
    return httpClient.accounts.me.$get().then(user => {
      authStore.changeUserInfo(user);
    });
  }

  fetchUserData() {
    return httpClient.accounts.me.$get();
  }

  /**
   * ユーザー情報を更新する
   * @param user
   * @returns {Promise}
   */
  patchUser(user: Partial<EditableUserItems>) {
    return httpClient.accounts.me.$patch({
      body: user
    });
  }

  /**
   * ユーザー情報を登録する
   * 初回の登録フロー時に利用される
   * @param user
   * @returns {Promise}
   */
  registerUserInformation(user: Partial<EditableUserItems>) {
    const isV2Migrated = authStore.user?.isV2Migrated;
    // v2への移行が完了していないユーザー
    if (!isV2Migrated) {
      return httpClient.accounts.me.account_migration.$patch({
        body: user
      });
    }
    return httpClient.accounts.me.$patch({
      body: user
    });
  }

  fetchJobChangeList() {
    return httpClient.accounts.job_change.$get().then(res => res.results);
  }

  fetchQualifications() {
    return httpClient.accounts.me.qualifications.$get();
  }

  fetchQualification(qualificationId: number) {
    return httpClient.accounts.me.qualifications._id(qualificationId).$get();
  }

  createQualifications(image: string | null, qualification: number) {
    return httpClient.accounts.me.qualifications.$post({
      body: { image, qualification }
    });
  }

  patchQualifications(
    qualificationId: number,
    image: string | null | undefined,
    qualification: number
  ) {
    return httpClient.accounts.me.qualifications
      ._id(qualificationId)
      .$patch({ body: { image, qualification } });
  }

  updateVerifiedQualificationImage(id: number, image: string) {
    return httpClient.accounts.me.qualifications
      ._id(id)
      .images.$patch({ body: { image } });
  }

  deleteQualifications(qualificationId: number) {
    return httpClient.accounts.me.qualifications._id(qualificationId).$delete();
  }

  fetchQualificationFirstVerifiedAt() {
    return httpClient.accounts.me.qualification_first_verified_at.$get();
  }

  fetchFavoriteOffices(
    query: Partial<{
      limit: number;
    }> = { limit: 100 }
  ) {
    return httpClient.accounts.me.favorite_offices.$get({
      query: {
        // eslint-disable-next-line
        is_favorite: "true",
        limit: query?.limit
      }
    });
  }

  patchFavoriteOffice(favoriteId: number, isFavorite: boolean) {
    return httpClient.accounts.me.favorite_offices._id(favoriteId).$patch({
      body: {
        isFavorite
      }
    });
  }

  registerDeviceToken(
    deviceToken: string,
    platform: string | null,
    appVersion: string | null
  ): Promise<boolean> {
    console.warn("_deviceToken", deviceToken);
    return httpClient.accounts.me.fcm_token
      .$post({
        body: {
          // eslint-disable-next-line
          fcm_token: deviceToken,
          platform: platform,
          app_version: appVersion
        }
      })
      .then(() => true)
      .catch(err => {
        captureException(err as Error);
        return false;
      });
  }

  fetchWholeAccounts() {
    return httpClient.accounts.me.whole_accounts
      .$get({
        query: { limit: 9999 }
      })
      .then(res => res.results);
  }

  initialProfileRegister() {
    return httpClient.accounts.me.initial_profile_register.$post();
  }

  requestVerification() {
    return httpClient.accounts.me.request_verification.$post();
  }

  fetchLatestRequestVerificationQueue() {
    return httpClient.accounts.verification_request_queue
      .$get()
      .then(res => res?.results[0]);
  }

  requestVerificationQueue(body: Partial<RequestVerificationQueueBody>) {
    return httpClient.accounts.verification_request_queue.$post({ body });
  }

  submitVerification() {
    return httpClient.accounts.verification_request_queue.submit.$post();
  }

  fetchReferPromotion() {
    return httpClient.accounts.refer_promotion.$get();
  }

  fetchReferPromotionLog() {
    return httpClient.accounts.refer_promotion_log.$get({
      query: {
        is_refer: true
      }
    });
  }

  fetchReferredPromotionLog() {
    return httpClient.accounts.refer_promotion_log.$get({
      query: {
        is_referred: true
      }
    });
  }

  applyReferPromotionCode(body: ReferPromotionApply) {
    return httpClient.accounts.refer_promotion.apply.$post({ body });
  }

  async migrateWorkerService(toWorkerServiceId: number) {
    return await httpClient.accounts.me.migrate_worker_service.$post({
      body: {
        toWorkerServiceId
      }
    });
  }

  fetchPhoneNumber() {
    return httpClient.accounts.me.phone_number.$get();
  }

  updatePhoneNumber(body: { smsSessionId: string; phoneNumber: string }) {
    return httpClient.accounts.me.phone_number.$patch({ body });
  }

  fetchFirstBootSurvey(fcmToken: string) {
    return httpClient.accounts.first_boot_survey._id(fcmToken).$get();
  }

  fetchFirstBootSurveyV2(fcmToken: string) {
    return httpClient.accounts.v2.first_boot_survey._id(fcmToken).$get();
  }

  registerFirstBootSurveyV2(
    fcmToken: string | null,
    worker_service: number,
    prefecture: number,
    locality: number,
    longitude: number | null,
    latitude: number | null
  ) {
    return httpClient.accounts.v2.first_boot_survey.$post({
      body: {
        fcmToken,
        worker_service,
        prefecture,
        locality,
        longitude,
        latitude
      }
    });
  }

  fetchModifiedStatus() {
    return httpClient.accounts.verification_request_queue.modified_status.$get();
  }

  shouldShowAppReviewModal() {
    return httpClient.accounts.app_satisfaction.should_show_app_satisfaction.$get();
  }

  submitAppReview(appSatisfaction: AppSatisfaction) {
    return httpClient.accounts.app_satisfaction.$post({
      body: appSatisfaction
    });
  }

  fetchCancelRate() {
    return httpClient.accounts.me.cancel_rate.$get();
  }

  /**
   * 続柄を取得する
   */
  fetchRelationshiopToWorker() {
    return httpClient.accounts.relationship_to_worker.$get();
  }

  fetchEmergencyContactOfRelationship() {
    return httpClient.accounts.me.emergency_contact_of_relationship.$get();
  }

  createEmergencyContactOfRelationship(
    phoneNumber: string,
    relationshipToWorker: number
  ) {
    return httpClient.accounts.me.emergency_contact_of_relationship.$post({
      body: {
        phoneNumber,
        relationshipToWorker
      }
    });
  }

  updateEmergencyContactOfRelationship(
    phoneNumber: string,
    relationshipToWorker: number
  ) {
    return httpClient.accounts.me.emergency_contact_of_relationship.$patch({
      body: {
        phoneNumber,
        relationshipToWorker
      }
    });
  }

  /**
   * アプリ内レビューを表示するべきかを判定する
   * @param timing アプリ内レビューの表示タイミング
   */
  shouldShowInAppReview(timing: TimingOfInAppReview, osType: OSType) {
    return httpClient.accounts.in_app_review.should_show_in_app_review.$post({
      body: {
        timing,
        osType
      }
    });
  }

  /**
   * アプリバッジ件数を最新化する
   */
  async refreshAppBadgeCount() {
    const { badgeCount } = await httpClient.accounts.me.app_badge_count.$get();
    if (window.cordova) {
      window.cordova.plugins.notification.badge.set(badgeCount);
    }
  }

  fetchPointHistoryList(
    query?: PageableRequest<{
      operation_type: string;
    }>
  ) {
    return httpClient.accounts.me.point_history.$get({
      query
    });
  }

  /**
   * アプリ起動に関する情報を記録する
   * 現状は日時のみ記録しているため引数は無し
   * @returns Promise
   */
  recordAppBoot() {
    return httpClient.accounts.me.app_boot.$post({ body: {} });
  }

  fetchMutedOffices(
    query?: Partial<{
      limit: number;
      offset: number;
      isMuted: boolean;
      officeId: number;
      ordering: string;
    }>
  ) {
    return httpClient.accounts.me.mute_offices.$get({
      query: {
        limit: query?.limit,
        offset: query?.offset,
        is_muted: query?.isMuted,
        office: query?.officeId,
        ordering: query?.ordering
      }
    });
  }

  canMuteOffice(officeId: number) {
    return httpClient.accounts.me.mute_offices.can_mute.$get({
      query: {
        office_id: officeId
      }
    });
  }

  updateMutedOffice({
    officeId,
    isMuted,
    reason
  }: {
    officeId: number;
    isMuted: boolean;
    reason?: string;
  }) {
    return httpClient.accounts.me.mute_offices.status.$post({
      body: {
        office_id: officeId,
        is_muted: isMuted,
        reason
      }
    });
  }

  /**
   * ユーザーのuuidが登録されていなければuuidを登録する
   */
  async registerUuidIfNeeded() {
    if (!authStore.sessionId) return;

    const uuid = authStore.user?.uuid;
    if (uuid && uuid.length > 0) return;

    const user = await this.fetchUserData();
    if (user.uuid && user.uuid.length > 0) return;

    this.patchUser({ uuid: authStore.sessionId });
    const updatedUser = Object.assign({}, user);
    updatedUser.uuid = authStore.sessionId;
    authStore.changeUserInfo(updatedUser);
  }

  // TODO: hasAnyQualification を消すとこちらも消せる
  getQualifications(): Qualification[] {
    const user = authStore.user;
    if (!user) return [];
    return user.qualifications.map(qualification => {
      const q = masterStore.visibleQualifications.find(
        masterQualification =>
          masterQualification.id === qualification.qualification
      );
      if (!q) throw new Error("qualification not found");
      return q;
    });
  }

  hasAnyQualification(qualificationSlugs: string[]): boolean {
    return accountService
      .getQualifications()
      .some(q => qualificationSlugs.includes(q.slug));
  }

  async fetchDesiredWorkSchedules(): Promise<DesiredWorkSchedule[]> {
    return await httpClient.accounts.me.desired_work_schedules.$get();
  }

  async updateDesiredWorkSchedule(
    desiredWorkSchedule: DesiredWorkSchedule
  ): Promise<DesiredWorkSchedule[]> {
    return await httpClient.accounts.me.desired_work_schedules.update.$post({
      body: desiredWorkSchedule
    });
  }

  async deleteDesiredWorkSchedule(
    date: string
  ): Promise<DesiredWorkSchedule[]> {
    return await httpClient.accounts.me.desired_work_schedules.delete.$delete({
      body: {
        date
      }
    });
  }
}

export const accountService = new AccountService();
